import {
  Car,
  Cog,
  Home,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import type { LeadQuote, Vehicle } from "@ir/types";
import { formatCurrency, formatDate, formatYesNo } from "@ir/services";
import { Badge, Card, CardHeader, DetailList } from "@ir/ui";

export function VehiclePanel({
  vehicle,
  quote,
  index,
}: {
  vehicle: Vehicle;
  quote: LeadQuote;
  index: number;
}) {
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(" ") || `Vehicle ${index + 1}`;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          icon={Car}
          title={title}
          description={vehicle.year ? `${vehicle.year}` : undefined}
          actions={
            vehicle.vrm ? (
              // UK number plates have a recognised look; reproducing it makes
              // the registration findable at a glance.
              <span className="rounded border border-gold-500/70 bg-gold-100 px-2.5 py-1 font-mono text-sm font-bold uppercase tracking-wider text-ink-900 dark:bg-gold-900/60 dark:text-gold-100">
                {vehicle.vrm}
              </span>
            ) : undefined
          }
        />
        <DetailList
          items={[
            { label: "Engine", value: vehicle.engine ? `${vehicle.engine}cc` : "" },
            { label: "Fuel", value: vehicle.fuel_type },
            { label: "Transmission", value: vehicle.transmission },
            { label: "Weight", value: vehicle.weight ? `${vehicle.weight}kg` : "" },
            { label: "Use", value: vehicle.use },
            { label: "Value", value: formatCurrency(vehicle.value) },
          ]}
        />
      </Card>

      <Card>
        <CardHeader icon={ShieldCheck} title="Cover and no-claims bonus" />
        <DetailList
          items={[
            { label: "Cover type", value: vehicle.cover?.type },
            { label: "Start date", value: formatDate(vehicle.cover?.date) },
            {
              label: "Windscreen cover",
              value: formatYesNo(quote.risk?.wind_screen ?? quote.wind_screen),
            },
            { label: "Has NCB", value: formatYesNo(vehicle.ncb?.have) },
            ...(vehicle.ncb?.have
              ? [
                  { label: "NCB years", value: vehicle.ncb.years },
                  { label: "Earned on", value: vehicle.ncb.type },
                  {
                    label: "Policy expiry",
                    value: formatDate(
                      vehicle.ncb.policy_expire_date ?? vehicle.ncb.current_policy_end_date,
                    ),
                    hideWhenEmpty: true,
                  },
                  {
                    label: "Protected",
                    value: formatYesNo(vehicle.ncb.protected),
                    hideWhenEmpty: true,
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card>
        <CardHeader icon={Cog} title="Specification and ownership" />
        <DetailList
          items={[
            { label: "Purchase date", value: formatDate(vehicle.purchase_date) },
            { label: "Owner", value: vehicle.owner },
            { label: "Registered keeper", value: vehicle.keeper },
            { label: "ABI group", value: vehicle.abi_group },
            { label: "Seats", value: vehicle.seats },
            { label: "Doors", value: vehicle.doors },
            { label: "Body type", value: vehicle.body_type },
            { label: "Leased", value: formatYesNo(vehicle.is_leased), hideWhenEmpty: true },
          ]}
        />
      </Card>

      <Card>
        <CardHeader icon={Sparkles} title="Additional properties" />
        <div className="flex flex-wrap gap-2">
          <PropertyChip label="Imported" active={vehicle.is_imported} />
          <PropertyChip label="Left-hand drive" active={vehicle.is_left_hand_drive} />
          <PropertyChip label="Carries hazardous goods" active={vehicle.carried_hazardous} />
          <PropertyChip label="Carries signage" active={vehicle.carries_signage} />
        </div>
      </Card>

      <Card>
        <CardHeader icon={Home} title="Overnight parking" />
        <DetailList
          items={[
            {
              label: "Kept at home address",
              value: formatYesNo(vehicle.overnight_same_address),
            },
            { label: "Location", value: vehicle.overnight_location },
            ...(vehicle.overnight_same_address
              ? []
              : [
                  {
                    label: "Location postcode",
                    value: vehicle.overnight_location_postcode,
                    hideWhenEmpty: true,
                  },
                ]),
          ]}
        />
      </Card>

      <Card>
        <CardHeader icon={Wrench} title="Modifications" />
        {vehicle.has_modification && vehicle.modification?.length ? (
          <ul className="flex flex-wrap gap-2">
            {vehicle.modification.map((modification, modIndex) => (
              <li key={modIndex}>
                <Badge tone="warning" icon={Wrench}>
                  {modification.type ?? "Unspecified"}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-content-muted">No modifications declared.</p>
        )}
      </Card>
    </div>
  );
}

/**
 * Boolean risk factors read faster as present/absent chips than as a column of
 * "No" values.
 */
function PropertyChip({
  label,
  active,
}: {
  label: string;
  active: boolean | null | undefined;
}) {
  return (
    <Badge tone={active ? "warning" : "neutral"} dot>
      {label}: {active ? "Yes" : "No"}
    </Badge>
  );
}
